import { makeStyles } from '@material-ui/core/styles';
import _uniq from 'lodash/uniq';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import IcHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import { setNodeById, setSelectedNode } from '../../../features/mainSlice';
import { findTag } from '../../../features/organizationSlice';
import AutoCompleted from '../../shared/AutoCompleted';
import Tag from '../../shared/Tag';
import Tooltip from '../../shared/Tooltip';

const useStyles = makeStyles(
  {
    title: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      marginBottom: 7,
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 4,
      },
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8,
    },
    dropdown: {
      minWidth: 120,
      marginRight: 8,
    },
    add: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '15px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$2,
      cursor: 'pointer',
    },
    minus: {
      marginLeft: 8,
    },
    tag: {
      marginBottom: 8,
    },
    targetInput: {
      width: '100%',
    },
  },
  {
    name: 'Tags',
  },
);

// tag condition type => TagFilterType

const maxTagConditionLength = 20;

const Tags = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedNode, isErrorNodeIds } = useSelector(state => state.ma);
  const { objectId: orgId, tagCandidates } = useSelector(
    store => store.organization,
  );
  const { data } = selectedNode || {};
  const { conditions } = data || {};
  const isError = !!~isErrorNodeIds.indexOf(selectedNode?.id);

  const handleChangeTag = tagName => {
    dispatch(findTag(tagName, orgId));
  };

  const onRemoveValue = (conditionIndex, index) => {
    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: conditions.map((c, i) => {
          if (i === conditionIndex) {
            return {
              ...c,
              value: c?.value.filter((v, vi) => vi !== index),
            };
          }
          return c;
        }),
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const onChangeTag = (value, index) => {
    if ((conditions[index]?.value || []).length >= maxTagConditionLength) {
      return;
    }

    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: conditions.map((c, i) => {
          if (i === index) {
            return { ...c, value: _uniq([...c?.value, value]) };
          }
          return c;
        }),
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const conditionsComponents = useMemo(() => {
    return (conditions || []).map((condition, index) => {
      return (
        <div key={`tags-components-${selectedNode?.id}-${index}`}>
          <div
            className={classes.row}
            key={`tags-components-${selectedNode?.id}-${index}`}
          >
            <AutoCompleted
              className={classes.targetInput}
              placeholder={t('trigger-setting.tags-placeholder')}
              candidates={tagCandidates.filter(
                c => !~(condition?.value || []).indexOf(c?.name),
              )}
              onFocus={() => {}}
              onChange={handleChangeTag}
              onEnter={value => onChangeTag(value, index)}
              isError={isError && !condition?.value.length}
              notNull
            />
          </div>
          <div>
            {(condition?.value || []).map((tag, idx) => (
              <Tag
                className={classes.tag}
                size="M"
                tag={tag}
                key={`tags-${index}${idx}`}
                clickable
                conditionIndex={index}
                idx={idx}
                color="colorPrimary"
                onDeleteFunction={onRemoveValue}
                deleteable
                isCondition
              />
            ))}
          </div>
        </div>
      );
    });
  }, [conditions, tagCandidates, isError]);

  useEffect(() => {
    dispatch(findTag('', orgId));
  }, []);

  return (
    <div>
      <div className={classes.title}>
        {t('trigger-setting.tags')}
        <Tooltip
          light
          placement="top"
          title={t('trigger-setting.tags-tooltip')}
        >
          <img className={classes.marginLeft8} src={IcHelpCircle} alt="alert" />
        </Tooltip>
      </div>
      {conditionsComponents}
    </div>
  );
};

export default Tags;
